import { type FolderListItem } from "../types/folder.types.ts"

interface FolderTreeNode {
  title: string
  value: string
  children?: FolderTreeNode[]
}

const childrenByParent = (
  folders: FolderListItem[]
): Map<string, FolderListItem[]> => {
  const map = new Map<string, FolderListItem[]>()
  for (const folder of folders) {
    const key = folder.parent ?? ""
    const list = map.get(key) ?? []
    list.push(folder)
    map.set(key, list)
  }
  return map
}

/** A `TreeSelect`-ready tree built from the project's full, flat folder list. */
const buildFolderTree = (folders: FolderListItem[]): FolderTreeNode[] => {
  const childrenOf = childrenByParent(folders)

  const toNode = (folder: FolderListItem): FolderTreeNode => {
    const children = childrenOf.get(folder.id) ?? []
    return {
      title: folder.name,
      value: folder.id,
      ...(children.length ? { children: children.map(toNode) } : {})
    }
  }

  return (childrenOf.get("") ?? []).map(toNode)
}

/** Every value a built tree can select; a folder whose parent was not listed is not among them. */
const folderTreeValues = (nodes: FolderTreeNode[]): string[] =>
  nodes.flatMap((node) => [
    node.value,
    ...folderTreeValues(node.children ?? [])
  ])

/** The folder itself, plus every descendant when subfolders are included. */
const collectFolderIds = (
  rootId: string,
  folders: FolderListItem[],
  includeDescendants: boolean
): Set<string> => {
  if (!includeDescendants) return new Set([rootId])

  const childrenOf = childrenByParent(folders)
  const result = new Set<string>([rootId])
  const queue = [rootId]
  for (let i = 0; i < queue.length; i++) {
    for (const child of childrenOf.get(queue[i]) ?? []) {
      if (!result.has(child.id)) {
        result.add(child.id)
        queue.push(child.id)
      }
    }
  }
  return result
}

export { buildFolderTree, collectFolderIds, folderTreeValues }
export type { FolderTreeNode }
